import { ReportRequest } from '@/client/reports/types/reports.type';
import { RedirectPaths, RedirectPathsEnum } from '@/common/constants';
import { useUsersWithOverdueCoursesReports } from '@/hooks/query/dashboard.hooks';
import { useAppSelector } from '@/hooks/store/store.hooks';
import { selectCurrentAccount } from '@/store/features/account/current-account.slice';
import { AccordionTabHeader } from '@/ui/accordion/AccordionTabHeader';
import { AppButton } from '@/ui/buttons';
import { OverdueUsersChart } from '@/ui/charts/bar-chart/OverdueUsersChart';
import { overdueDemoChartData } from '@/ui/charts/charts.helper';
import { FlexContainer } from '@/ui/styled-ui';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { ChartEmptyState, ChartEmptyStateType } from './ChartEmptyState';

const ChartContainer = styled(FlexContainer)`
  width: 100%;
  max-height: 510px;
  margin-top: 0;
  flex-direction: column;
  justify-content: space-between;
  align-items: center;
  border: 1px solid var(--gray-medium);
  border-radius: var(--xsmall-border-radius);
  padding: var(--default-padding);
  background: var(--white-main);
  flex: 1 1 40%;
`;

const DisabledContent = styled.div`
  width: 100%;
  margin-top: var(--default-padding);
  padding: 0 var(--big-padding);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  gap: var(--small-padding);
`;

const DisabledTitle = styled.h3`
  font-size: var(--heading-small-font-size);
  line-height: var(--heading-small-line-height);
  margin: 0;
`;

const DisabledDescription = styled.p`
  margin: 0;
  max-width: 520px;
  font-size: var(--small-font-size);
  line-height: var(--small-line-height);
`;

type OverdueUsersProps = {
  filters: ReportRequest;
};

export const OverdueUsers = ({ filters }: OverdueUsersProps) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const account = useAppSelector(selectCurrentAccount);
  const isEnabled = account?.courseDueDays && account.courseDueDays > 0;

  const {
    usersWithOverdueCourses,
    isLoading: areUsersWithOverdueCoursesLoading,
  } = useUsersWithOverdueCoursesReports(account?.id as string, filters);

  const handleTurnOnDueDates = () => {
    if (!account?.id) {
      return;
    }

    const redirectPath = account?.isSystem
      ? RedirectPaths[RedirectPathsEnum.EDIT_ACCOUNT](account.id)
      : RedirectPaths[RedirectPathsEnum.ACCOUNT](account.id);

    navigate(redirectPath);
  };

  return (
    <ChartContainer id="overdue-users-container">
      <AccordionTabHeader
        title={isEnabled ? t('dashboard.awareness.overdue.title') : undefined}
        description={
          isEnabled ? t('dashboard.awareness.overdue.description') : undefined
        }
        isSubHeader
      />
      {!isEnabled ? (
        <>
          <DisabledContent>
            <DisabledTitle>
              {t('dashboard.awareness.overdue.title')}
            </DisabledTitle>
            <DisabledDescription>
              {t('dashboard.awareness.overdue.disabled.description')}
            </DisabledDescription>
          </DisabledContent>
          <OverdueUsersChart
            id="overdue-users-disabled"
            reportData={overdueDemoChartData}
            isDisabled
            disabledOverlayContent={
              <AppButton
                id="overdue-users-turn-on-due-dates"
                label={t('dashboard.awareness.overdue.disabled.cta')}
                onClick={handleTurnOnDueDates}
              />
            }
          />
        </>
      ) : !areUsersWithOverdueCoursesLoading &&
        (!usersWithOverdueCourses?.result ||
          usersWithOverdueCourses.result.length === 0) ? (
        <ChartEmptyState
          minHeight={375}
          noMargin
          noBorder
          type={ChartEmptyStateType.AWT}
          title={t('dashboard.awareness.overdue.empty.title')}
          description={t('dashboard.awareness.overdue.empty.description')}
        />
      ) : (
        <OverdueUsersChart
          id="overdue-users"
          reportData={usersWithOverdueCourses?.result}
          isLoading={areUsersWithOverdueCoursesLoading}
          fromDate={filters.from}
          toDate={filters.to}
        />
      )}
    </ChartContainer>
  );
};
